import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { WelderDetailedAnalysis } from '../../types/stats';
import { PipeSelector } from '../../../../components/ui/PipeSelector';

interface IndividualWelderAnalysisProps {
  data: WelderDetailedAnalysis;
}

export const IndividualWelderAnalysis = ({ data }: IndividualWelderAnalysisProps) => {
  const welders = Object.keys(data.welderDetails).sort();
  const [selectedWelder, setSelectedWelder] = useState<string>(welders[0] || '');

  const welderData = useMemo(() => {
    if (!selectedWelder || !data.welderDetails[selectedWelder]) {
      return null;
    }
    return data.welderDetails[selectedWelder];
  }, [selectedWelder, data]);

  const defectTypeOption = useMemo(() => {
    if (!welderData || !data.allDefectTypes || data.allDefectTypes.length === 0) return null;

    // Use all defect types from the dataset, not just the ones this welder has
    const defectTypes = [...data.allDefectTypes].sort();
    const values = defectTypes.map((defect) => welderData.defectTypes[defect] || 0);

    const maxValue = Math.max(...values, 1);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          return `${params.name}: ${params.value}`;
        },
      },
      title: {
        text: `Defect Types - Welder ${selectedWelder}`,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      radar: {
        indicator: defectTypes.map((defect) => ({
          name: defect,
          max: maxValue,
        })),
        center: ['50%', '55%'],
        radius: '70%',
        startAngle: 90, // Start from top
        nameGap: 8, // Distance between label and axis
        axisName: {
          color: '#333',
          fontSize: 12,
          fontWeight: 'bold',
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(250, 250, 250, 0.3)', 'rgba(200, 200, 200, 0.3)'],
          },
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
      },
      series: [
        {
          name: 'Defect Types',
          type: 'radar',
          data: [
            {
              value: values,
              name: `Welder ${selectedWelder}`,
              areaStyle: {
                color: 'rgba(14, 165, 233, 0.3)',
              },
              lineStyle: {
                color: '#0ea5e9',
                width: 2,
              },
              label: {
                show: true,
                formatter: (params: any) => {
                  return params.value > 0 ? params.value : '';
                },
                fontSize: 11,
                fontWeight: 'bold',
                color: '#0ea5e9',
              },
            },
          ],
        },
      ],
    } as echarts.EChartsOption;
  }, [welderData, selectedWelder, data.allDefectTypes]);

  // Helper function to sort positions in clock order
  const sortClockOrder = (positions: string[]): string[] => {
    // Extract numeric value from position string
    const getPositionNumber = (pos: string): number => {
      const normalized = pos.toLowerCase().trim();
      const match = normalized.match(/(\d+)/);
      if (match) {
        const num = parseInt(match[1]);
        if (num >= 1 && num <= 12) {
          return num === 12 ? 12 : num;
        }
      }
      return 99; // Unknown positions go to end
    };

    return positions.sort((a, b) => {
      const aNum = getPositionNumber(a);
      const bNum = getPositionNumber(b);
      
      // Handle 12 specially - it should come first
      if (aNum === 12 && bNum !== 12) return -1;
      if (bNum === 12 && aNum !== 12) return 1;
      if (aNum === 12 && bNum === 12) return 0;
      
      // For others, sort numerically
      return aNum - bNum;
    });
  };

  const positionOption = useMemo(() => {
    if (!welderData || !data.allPositions || data.allPositions.length === 0) return null;

    // Use all positions from the dataset, not just the ones this welder has
    const allPositions = [...data.allPositions];
    const sortedPositions = sortClockOrder(allPositions);
    
    // To get clockwise order starting from 12 o'clock:
    // ECharts places indicators counter-clockwise, so we need [12, 11, 10, ..., 2, 1]
    // This way: 12 at top, then going counter-clockwise gives us 12, 11, 10, ..., 2, 1
    // But we want 12, 1, 2, ..., 11 clockwise, so we reverse except for 12
    const reversedForClockwise = sortedPositions.length > 1 
      ? [sortedPositions[0], ...sortedPositions.slice(1).reverse()]
      : sortedPositions;
    
    // Map values for all positions in the reversed order, using 0 for positions the welder doesn't have
    const sortedValues = reversedForClockwise.map((pos) => {
      // Try exact match first
      let value = welderData.positions[pos] || 0;
      
      // If not found, try case-insensitive match
      if (value === 0) {
        const matchingKey = Object.keys(welderData.positions).find(
          key => key.toLowerCase().trim() === pos.toLowerCase().trim()
        );
        if (matchingKey) {
          value = welderData.positions[matchingKey];
        }
      }
      
      return value;
    });

    const maxValue = Math.max(...sortedValues, 1);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          return `${params.name}: ${params.value}`;
        },
      },
      title: {
        text: `Positions - Welder ${selectedWelder}`,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      radar: {
        indicator: reversedForClockwise.map((position) => ({
          name: position,
          max: maxValue,
        })),
        center: ['50%', '50%'],
        radius: '70%',
        startAngle: 90, // Start from top (12 o'clock)
        nameGap: 20, // Distance between label and axis
        axisName: {
          color: '#333',
          fontSize: 12,
          fontWeight: 'bold',
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(250, 250, 250, 0.3)', 'rgba(200, 200, 200, 0.3)'],
          },
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
      },
      series: [
        {
          name: 'Positions',
          type: 'radar',
          data: [
            {
              value: sortedValues,
              name: `Welder ${selectedWelder}`,
              areaStyle: {
                color: 'rgba(34, 197, 94, 0.3)',
              },
              lineStyle: {
                color: '#22c55e',
                width: 2,
              },
              label: {
                show: true,
                formatter: (params: any) => {
                  return params.value > 0 ? params.value : '';
                },
                fontSize: 11,
                fontWeight: 'bold',
                color: '#22c55e',
              },
            },
          ],
        },
      ],
    } as echarts.EChartsOption;
  }, [welderData, selectedWelder, data.allPositions]);

  if (welders.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No welder data available.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-heading text-[14px] font-semibold text-blueprint-blue">Individual Welder Analysis</h4>
        <PipeSelector
          value={selectedWelder}
          onChange={setSelectedWelder}
          placeholder="Select welder"
          triggerWidth="w-full sm:w-[240px]"
          options={welders.map((welder) => ({
            value: welder,
            label: `Welder ${welder}`,
          }))}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {defectTypeOption && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <ReactECharts 
              option={defectTypeOption} 
              style={{ height: 400 }} 
              notMerge={true}
              lazyUpdate={true}
            />
          </div>
        )}
        {positionOption && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <ReactECharts 
              option={positionOption} 
              style={{ height: 400 }} 
              notMerge={true}
              lazyUpdate={true}
            />
          </div>
        )}
        {!defectTypeOption && !positionOption && (
          <div className="col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500 text-center">
            No data available for selected welder.
          </div>
        )}
      </div>
    </div>
  );
};

